import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft, Headphones, Mic, BookOpen, PenTool } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrintExam = () => {
  const { gradeLevel } = useParams();
  const [searchParams] = useSearchParams();
  const studentName = searchParams.get("student") || "";
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  const gradeLabels = {
    "K": "Kindergarten", "1": "Grade 1", "2": "Grade 2",
    "3-4": "Grades 3-4", "5-6": "Grades 5-6", "7-8": "Grades 7-8", "9-12": "Grades 9-12"
  };

  const modalityOrder = ["listening", "reading", "writing"];
  const modalityIcons = { listening: Headphones, speaking: Mic, reading: BookOpen, writing: PenTool };

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await axios.get(`${API}/questions?grade_level=${gradeLevel}`);
        setQuestions(res.data.questions || []);
      } catch (err) {
        console.error("Error fetching questions:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [gradeLevel]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner w-10 h-10 border-[#002FA7] border-t-transparent"></div>
      </div>
    );
  }

  const grouped = {};
  questions.forEach(q => {
    if (!grouped[q.modality]) grouped[q.modality] = [];
    grouped[q.modality].push(q);
  });

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="print-exam-page">
      {/* Controls (hidden in print) */}
      <div className="no-print mb-8 flex items-center justify-between">
        <Link to={`/exam/setup/${gradeLevel}`}>
          <Button variant="outline" className="btn-sharp h-11">
            <ArrowLeft size={18} className="mr-2" />
            Back to Setup
          </Button>
        </Link>
        <Button onClick={handlePrint} className="btn-sharp-primary h-11 px-6" data-testid="print-exam-btn">
          <Printer size={18} className="mr-2" />
          Print Exam
        </Button>
      </div>

      {/* Print Header */}
      <div className="border-2 border-black p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MOSAIC Teacher Edition — Practice Exam
            </h1>
            <p className="text-gray-600">{gradeLabels[gradeLevel] || `Grade ${gradeLevel}`}</p>
          </div>
          <div className="text-right text-sm text-gray-500">
            <div className="font-bold text-black">MOSAIC</div>
            <div>NY State Preparation</div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-300 grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Student: </span>
            <span className="font-semibold">{studentName || "________________________"}</span>
          </div>
          <div>
            <span className="text-gray-500">Date: </span>
            <span className="font-semibold">{new Date().toLocaleDateString()}</span>
          </div>
          <div>
            <span className="text-gray-500">Teacher: </span>
            <span>________________________</span>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-gray-50 border-2 border-gray-200 p-5 mb-8 text-sm">
        <h2 className="font-bold mb-2">Instructions</h2>
        <ul className="space-y-1 text-gray-700">
          <li>- For Listening questions, the teacher reads the stimulus aloud or plays the audio.</li>
          <li>- For Reading questions, students read the passage and answer the questions.</li>
          <li>- For Writing questions, students write their response in the space provided.</li>
          <li>- Speaking section is administered individually and scored by the teacher.</li>
          <li>- Circle or mark the correct answer for multiple choice questions.</li>
        </ul>
      </div>

      {/* Questions by Modality */}
      {modalityOrder.map(modality => {
        const qs = grouped[modality] || [];
        if (qs.length === 0) return null;
        const Icon = modalityIcons[modality];
        
        return (
          <div key={modality} className="mb-8 print-page-break" data-testid={`print-section-${modality}`}>
            {/* Modality Header */}
            <div className="bg-gray-900 text-white p-4 flex items-center gap-3 mb-4">
              <Icon size={22} />
              <h2 className="text-lg font-bold uppercase tracking-wider">
                {modality} — {qs.length} Questions
              </h2>
            </div>

            {/* Questions */}
            <div className="space-y-6">
              {qs.map((q, idx) => {
                const prevQ = idx > 0 ? qs[idx - 1] : null;
                const showStimulus = modality === "listening" 
                  ? (!prevQ || prevQ.big_picture_context !== q.big_picture_context)
                  : modality === "reading"
                  ? (!prevQ || prevQ.passage_title !== q.passage_title)
                  : true;
                // Determine Part label (A, B, C based on context groups)
                const contexts = [...new Set(qs.map(x => x.big_picture_context || x.passage_title || x.question_text))];
                const currentCtx = q.big_picture_context || q.passage_title || q.question_text;
                const partIdx = contexts.indexOf(currentCtx);
                const partLetter = String.fromCharCode(65 + Math.min(partIdx, 25));
                const partLabel = q.big_picture_context || q.passage_title || '';
                const showPartLabel = showStimulus && partLabel;
                
                // Check if this is the last question in a context group (show STOP sign)
                const nextQ = idx < qs.length - 1 ? qs[idx + 1] : null;
                const isLastInGroup = !nextQ || 
                  (modality === 'listening' && nextQ.big_picture_context !== q.big_picture_context) ||
                  (modality === 'reading' && nextQ.passage_title !== q.passage_title) ||
                  (modality !== 'listening' && modality !== 'reading');
                const showStop = isLastInGroup;

                // Sample question (first question of first Part gets "S" treatment)
                const isSampleQ = idx === 0;
                const isK2 = ['K','1','2'].includes(q.grade_level);
                const isUpper = ['7-8','9-12'].includes(q.grade_level);
                
                // Writing sentence count guidance
                const writingGuidance = q.question_type === 'constructed_response' || q.question_type === 'open_response'
                  ? (q.question_text?.toLowerCase().includes('list') || q.question_text?.toLowerCase().includes('name'))
                    ? 'Write your list below:'
                    : (q.question_text?.toLowerCase().includes('describe'))
                    ? 'Write 2 to 4 sentences.'
                    : isK2 ? 'Write 1 to 3 sentences.'
                    : isUpper ? 'Write 5 to 8 sentences.'
                    : 'Write 3 to 5 sentences.'
                  : null;
                
                // Writing line count based on grade/type
                const lineCount = q.question_type === 'constructed_response' || q.question_type === 'open_response'
                  ? (q.question_text?.toLowerCase().includes('list') || q.question_text?.toLowerCase().includes('name'))
                    ? 4
                    : isK2 ? 6
                    : isUpper ? 16
                    : 10
                  : 0;

                return (
                  <div key={q.id} className="print-no-break">
                    {/* ═══ Part Label Bar (WIDA format: dark gray bar with white text) ═══ */}
                    {showPartLabel && (
                      <div className="bg-gray-800 text-white px-4 py-2 mb-3 flex items-center gap-2" data-testid={`part-label-${partLetter}`}>
                        <span className="font-bold text-sm">Part {partLetter}:</span>
                        <span className="text-sm">{partLabel}</span>
                      </div>
                    )}

                    {/* Big Picture / Passage (show once per group) */}
                    {showStimulus && modality === "listening" && q.big_picture_context && (
                      <div className="bg-blue-50 border-2 border-blue-200 mb-3">
                        {q.bp_image_url && (
                          <img src={q.bp_image_url} alt={q.big_picture_context} className="w-full h-56 object-contain border-b-2 border-blue-200" />
                        )}
                        <div className="p-4">
                          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Big Picture</div>
                          <p className="text-sm text-gray-800">
                            {q.stimulus?.replace(/^\[Big Picture \d+: /, '').replace(/\]$/, '')}
                          </p>
                        </div>
                      </div>
                    )}

                    {showStimulus && modality === "reading" && q.passage_title && (
                      <div className="bg-emerald-50 border-2 border-emerald-200 mb-3">
                        {q.graphic_support_url && (
                          <img src={q.graphic_support_url} alt={q.passage_title} className="w-full h-40 object-contain bg-gray-50 border-b-2 border-emerald-200" />
                        )}
                        <div className="p-4">
                          <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-1">
                            Passage: {q.passage_title}
                          </div>
                          <p className="text-sm text-gray-800 leading-relaxed">
                            {q.stimulus?.replace(/^\[Passage: [^\]]+\]\n*/, '').replace(/\[Image:[^\]]+\]/g, '')}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* ═══ "S" Sample Question (WIDA: first question is guided practice) ═══ */}
                    {isSampleQ && (
                      <div className="bg-amber-50 border-2 border-amber-300 p-3 mb-3 text-xs text-amber-800" data-testid="sample-question-banner">
                        <span className="font-bold">Sample Question:</span> This question is done together with the teacher as guided practice.
                      </div>
                    )}

                    {/* ═══ Labeled Images (Picture A, Picture B) for Writing ═══ */}
                    {showStimulus && modality === "writing" && q.graphic_support_url && (
                      <div className="mb-3 border border-gray-200 p-3">
                        <div className="flex gap-4 items-start">
                          <div className="text-center">
                            <div className="text-xs font-bold text-gray-600 mb-1">Picture A</div>
                            <img 
                              src={q.graphic_support_url} 
                              alt="Picture A" 
                              className={`object-contain border border-gray-300 ${isK2 ? 'w-48 h-48' : 'w-40 h-40'}`}
                              style={{ filter: 'grayscale(100%) contrast(1.1)' }}
                            />
                          </div>
                          {q.bp_image_url && q.bp_image_url !== q.graphic_support_url && (
                            <div className="text-center">
                              <div className="text-xs font-bold text-gray-600 mb-1">Picture B</div>
                              <img 
                                src={q.bp_image_url} 
                                alt="Picture B" 
                                className={`object-contain border border-gray-300 ${isK2 ? 'w-48 h-48' : 'w-40 h-40'}`}
                                style={{ filter: 'grayscale(100%) contrast(1.1)' }}
                              />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2 italic">Look at the picture{q.bp_image_url && q.bp_image_url !== q.graphic_support_url ? 's' : ''} above. Use {q.bp_image_url && q.bp_image_url !== q.graphic_support_url ? 'them' : 'it'} to help you write.</p>
                      </div>
                    )}

                    {/* Question */}
                    <div className="border border-gray-200 p-4">
                      <div className="flex items-start gap-3">
                        {/* Question number — "S" circle for sample, numbered for rest */}
                        <div className={`w-8 h-8 border-2 flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                          isSampleQ ? 'border-amber-500 rounded-full bg-amber-50 text-amber-700' : 'border-gray-400'
                        }`}>
                          {isSampleQ ? 'S' : q.question_number}
                        </div>
                        <div className="flex-1">
                          {/* Per-question support image for L/R (not writing — writing uses labeled images above) */}
                          {modality !== 'writing' && (q.graphic_support_url || (!q.big_picture_context && q.bp_image_url)) && (
                            <div className="mb-3 print-support-img">
                              <img
                                src={q.graphic_support_url || q.bp_image_url}
                                alt="Question support"
                                className={`object-contain border border-gray-300 bg-gray-50 ${isK2 ? 'w-48 h-48' : 'w-36 h-36'}`}
                                style={{ filter: 'grayscale(100%) contrast(1.1)' }}
                              />
                            </div>
                          )}
                          <p className="font-medium mb-3">{q.question_text}</p>
                          
                          {q.question_type === "multiple_choice" && q.options && (
                            <div className="grid grid-cols-2 gap-2">
                              {q.options.map((opt, optIdx) => (
                                <div key={optIdx} className="flex items-center gap-2 p-2 border border-gray-200">
                                  <div className="w-6 h-6 border-2 border-gray-400 rounded-full flex-shrink-0"></div>
                                  <span className="text-sm">{String.fromCharCode(65 + optIdx)}. {opt}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {(q.question_type === "constructed_response" || q.question_type === "open_response") && (
                            <>
                              {/* ═══ "Plan Your Writing" section (WIDA: Gr 4+ gets planning space) ═══ */}
                              {!isK2 && !q.question_text?.toLowerCase().includes('list') && !q.question_text?.toLowerCase().includes('name') && (
                                <div className="border border-dashed border-gray-300 p-3 mb-3 bg-gray-50" data-testid="plan-writing">
                                  <div className="text-xs font-bold text-gray-600 mb-1">Plan your writing</div>
                                  <p className="text-[10px] text-gray-500 mb-2">Use this space to organize your ideas. You can make notes, an outline, a web, or any other kind of graphic organizer.</p>
                                  <div className="min-h-[60px] border border-gray-200 bg-white"></div>
                                </div>
                              )}

                              {/* Sentence/word count guidance */}
                              {writingGuidance && (
                                <div className="text-xs font-bold text-gray-600 mb-2" data-testid="writing-guidance">{writingGuidance}</div>
                              )}

                              {/* ═══ Numbered writing lines (WIDA: exact line counts) ═══ */}
                              <div className="border-2 border-gray-300 p-4 bg-white">
                                <div className="space-y-5">
                                  {[...Array(lineCount)].map((_, i) => (
                                    <div key={i} className="flex items-end gap-2">
                                      <span className="text-[9px] text-gray-300 w-4 flex-shrink-0">{i + 1}</span>
                                      <div className="flex-1 border-b border-gray-300"></div>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {/* ═══ Self-Assessment Checklist (WIDA: Gr 4+ "Now check your writing") ═══ */}
                              {!isK2 && !q.question_text?.toLowerCase().includes('list') && !q.question_text?.toLowerCase().includes('name') && (
                                <div className="mt-3 border border-gray-200 p-3 bg-gray-50 text-xs" data-testid="self-assessment">
                                  <div className="font-bold text-gray-700 mb-1">Now check your writing. Ask yourself:</div>
                                  <div className="space-y-1 text-gray-600">
                                    {['Did I write a beginning?', 'Did I support my main points with details?', 'Did I write an ending?', 'Did I choose the best words for my writing?', 'Did I organize my writing?', 'Did I make my ideas clear?'].map((item, i) => (
                                      <div key={i} className="flex items-center gap-2">
                                        <div className="w-3.5 h-3.5 border border-gray-400 flex-shrink-0"></div>
                                        <span>{item}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* ═══ STOP Sign (WIDA: red octagon after last question in each Part) ═══ */}
                    {showStop && (
                      <div className="flex justify-end mt-2 mb-4" data-testid="stop-sign">
                        <div className="bg-red-600 text-white font-black text-lg px-4 py-2 flex items-center justify-center" style={{
                          clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
                          width: '64px', height: '64px', fontSize: '14px'
                        }}>
                          STOP
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Answer Sheet */}
      <div className="print-page-break border-2 border-black p-6 mt-8">
        <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Answer Sheet — {gradeLabels[gradeLevel]}
        </h2>
        <div className="grid grid-cols-2 gap-x-12 gap-y-1 text-sm">
          {questions.filter(q => q.question_type === "multiple_choice").map(q => (
            <div key={q.id} className="flex items-center gap-2 py-1 border-b border-gray-100">
              <span className="font-mono font-bold w-8">Q{q.question_number}</span>
              <span className="text-xs text-gray-400 capitalize w-16">{q.modality}</span>
              <div className="flex gap-3">
                {["A", "B", "C", "D"].map(letter => (
                  <div key={letter} className="w-6 h-6 border border-gray-400 rounded-full flex items-center justify-center text-xs">
                    {letter}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-400 mt-8 pt-4 border-t border-gray-200">
        MOSAIC Practice Exam • {gradeLabels[gradeLevel]} • Generated {new Date().toLocaleDateString()} • For classroom use only
      </div>
    </div>
  );
};

export default PrintExam;
